import { type CareerApplicationDetail, type CareerApplicationStatus } from '@utazzo/shared';
import { Download } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { type ApplicationListQuery } from '../careers.api';
import { ApplicantDetailDialog, downloadCv } from '../components/ApplicantDetailDialog';
import {
  useAdaptedApplicationsList,
  useDeleteApplication,
  useHrDashboard,
  useUpdateApplicationStatus,
} from '../use-careers';

import { type DataGridColumn } from '@/components/data-grid';
import { ListScreen } from '@/components/list-screen';
import { SoftPill } from '@/components/soft-pill';

/**
 * Enquiries tab: job applications submitted via the website "Apply now" popup.
 * Server-query driven (real /admin/careers/applications API), embedded inside
 * the career page editor's tab body.
 */

const STATUS_ORDER: CareerApplicationStatus[] = [
  'RECEIVED',
  'IN_REVIEW',
  'INTERVIEW',
  'OFFER',
  'REJECTED',
  'WITHDRAWN',
];

const STATUS_PILL: Record<CareerApplicationStatus, { tone: string; dot: string; label: string }> = {
  RECEIVED: { tone: 'bg-sky-50 text-sky-700', dot: 'bg-sky-500', label: 'Received' },
  IN_REVIEW: { tone: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500', label: 'In review' },
  INTERVIEW: { tone: 'bg-violet-50 text-violet-700', dot: 'bg-violet-500', label: 'Interview' },
  OFFER: { tone: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500', label: 'Offer' },
  REJECTED: { tone: 'bg-zinc-100 text-zinc-600', dot: 'bg-zinc-400', label: 'Rejected' },
  WITHDRAWN: { tone: 'bg-zinc-100 text-zinc-600', dot: 'bg-zinc-400', label: 'Withdrawn' },
};

// Multi-select: the "all" option is dropped (clearing every checkbox IS the
// empty state - see DataGridFilter.multi).
const STATUS_OPTIONS = STATUS_ORDER.map((s) => ({ value: s, label: STATUS_PILL[s].label }));

/** Rolling windows, in days. Matches `appliedWithin` on the list endpoint. */
const APPLIED_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
] as const;

const COLUMNS: ReadonlyArray<DataGridColumn<CareerApplicationDetail>> = [
  { id: 'name', header: 'Name', sortable: true, cell: (row) => row.name },
  { id: 'email', header: 'Email', cell: (row) => row.email },
  { id: 'phone', header: 'Phone', cell: (row) => row.phone },
  { id: 'positionTitle', header: 'Position', cell: (row) => row.positionTitle },
  {
    id: 'createdAt',
    header: 'Applied',
    sortable: true,
    cell: (row) => (
      <span className="text-(--fg-muted) tabular-nums">
        {new Date(row.createdAt).toLocaleString()}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    truncate: false,
    cell: (row) => {
      const b = STATUS_PILL[row.status] ?? {
        tone: 'bg-zinc-100 text-zinc-600',
        dot: 'bg-zinc-400',
        label: row.status,
      };
      return (
        <SoftPill className={b.tone} dot dotClassName={b.dot}>
          {b.label}
        </SoftPill>
      );
    },
  },
];

interface Props {
  canWrite: boolean;
}

export const EnquiriesTab = ({ canWrite }: Props): React.JSX.Element => {
  const updateStatus = useUpdateApplicationStatus();
  const del = useDeleteApplication();
  // Same candidate view the HR dashboard opens, so a row click means the same
  // thing on both surfaces.
  const [applicant, setApplicant] = useState<CareerApplicationDetail | null>(null);

  // Role options come from the recruitment snapshot, which already returns
  // every distinct positionTitle that has applications - including roles since
  // removed from the careers page, which a list built from the OPEN_POSITIONS
  // section would miss. Same endpoint + same role gate as this table.
  const snapshot = useHrDashboard();
  const positionOptions = useMemo(
    () => [
      { value: '', label: 'All roles' },
      ...(snapshot.data?.byPosition ?? []).map((p) => ({
        value: p.positionTitle,
        label: `${p.positionTitle} (${p.total})`,
      })),
    ],
    [snapshot.data],
  );

  return (
    <>
      <ListScreen<CareerApplicationDetail, ApplicationListQuery>
        embedded
        title="Enquiries"
        useList={useAdaptedApplicationsList}
        defaultSort={{ by: 'createdAt', dir: 'desc' }}
        defaultPageSize={20}
        toQuery={(state, base) => ({
          ...base,
          ...(state.filters.status ? { status: state.filters.status } : {}),
          ...(state.filters.positionTitle ? { positionTitle: state.filters.positionTitle } : {}),
          ...(state.filters.appliedWithin ? { appliedWithin: state.filters.appliedWithin } : {}),
        })}
        columns={COLUMNS}
        filters={[
          {
            id: 'status',
            label: 'Status',
            options: STATUS_OPTIONS,
            widthClass: 'w-44',
            // Multi so "everything still in play" is one chip rather than four
            // separate passes over the table.
            multi: true,
          },
          {
            id: 'positionTitle',
            label: 'Role',
            options: positionOptions,
            widthClass: 'w-60',
          },
          {
            id: 'appliedWithin',
            label: 'Applied',
            options: APPLIED_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
            widthClass: 'w-40',
          },
        ]}
        getRowId={(row) => row.id}
        onRowClick={(row) => setApplicant(row)}
        rowActions={{
          extra: (row) => [
            {
              type: 'item',
              label: 'Download CV',
              icon: <Download className="h-4 w-4" />,
              onSelect: () => void downloadCv(row.id),
            },
            ...(canWrite
              ? [
                  { type: 'separator' as const },
                  { type: 'label' as const, label: 'Set status' },
                  ...STATUS_ORDER.filter((s) => s !== row.status).map((s) => ({
                    type: 'item' as const,
                    label: STATUS_PILL[s].label,
                    onSelect: () => updateStatus.mutate({ id: row.id, status: s }),
                  })),
                ]
              : []),
          ],
          delete: canWrite
            ? {
                onDelete: (row) => del.mutateAsync(row.id),
                confirm: (row) => ({
                  title: 'Delete this enquiry?',
                  description: `${row.name}'s application for "${row.positionTitle}" will be removed.`,
                }),
              }
            : undefined,
        }}
        copy={{
          searchPlaceholder: 'Search by name, email or position',
          emptyTitle: 'No enquiries yet',
          emptyDescription: 'Applications from the website “Apply now” form will appear here.',
          errorTitle: 'Failed to load enquiries',
          errorDescription: 'Try refreshing or check your connection.',
        }}
      />
      <ApplicantDetailDialog
        applicant={applicant}
        onClose={() => setApplicant(null)}
        canWrite={canWrite}
      />
    </>
  );
};
